/*
 * ----------------------------------------------------------------------------
 * CUSTOM WISHLIST (Swym Wishlist Plus JS SDK)
 * ----------------------------------------------------------------------------
 *
 * Theme-native wishlist UI backed by the Swym JS SDK. The Swym "Wishlist App
 * Embed" loads the SDK and exposes it via window.SwymCallbacks, so no API key
 * is ever present in the storefront.
 *
 * Product-level wishlist: items are matched by product id (empi). The first
 * available variant id (epi) is sent because Swym keys items on the variant.
 *
 * Custom elements:
 *   <wishlist-button>  toggle a single product, reflects state via aria-pressed
 *   <wishlist-count>   header badge mirroring the number of saved products
 *   <wishlist-list>    drawer body, toggles empty-state vs rendered item rows
 */
(function () {
  'use strict';

  // Surfaces wishlist failures to the console (red error) and, when available,
  // to PostHog as a "wishlist_error" event so they can be tracked.
  function reportError(context, error, extra) {
    var detail = error instanceof Error ? error : new Error(String(error || context));
    console.error('[wishlist] ' + context, detail, extra || {});
    var ph = window.posthog;
    if (ph && typeof ph.capture === 'function') {
      try {
        var props = { context: context, message: detail.message };
        if (extra) {
          Object.keys(extra).forEach(function (key) {
            props[key] = extra[key];
          });
        }
        ph.capture('wishlist_error', props);
      } catch (telemetryError) {
        // Telemetry must never break the UI.
      }
    }
  }

  // Quiet, non-error trace for expected states (e.g. the SDK not loading because
  // the shopper has not accepted cookies). Never sent to PostHog.
  function logDebug(context, message) {
    if (typeof console !== 'undefined' && typeof console.debug === 'function') {
      console.debug('[wishlist] ' + context, message || '');
    }
  }

  // Reads Shopify's Customer Privacy API to tell whether the shopper has already
  // granted the consent the wishlist needs (functional / preferences cookies,
  // which gate the Swym SDK). Returns true or false, or null when the answer is
  // not yet known (API not loaded). The cookie notice must never ask a shopper
  // who has already consented to accept cookies again.
  function wishlistConsentGranted() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp) {
        return null;
      }
      if (typeof cp.preferencesProcessingAllowed === 'function') {
        return cp.preferencesProcessingAllowed();
      }
      if (typeof cp.userCanBeTracked === 'function') {
        return cp.userCanBeTracked();
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  // When the Customer Privacy API has not loaded at connect time (it emits no
  // ready event and visitorConsentCollected only fires on a fresh choice), the
  // drawer re-checks consent on this cadence. The total window (attempts *
  // interval) spans the 8s SDK boot timeout below, so an already-consented
  // returning visitor clears the cookie notice even if the SDK never boots.
  var CONSENT_RECHECK_ATTEMPTS = 16;
  var CONSENT_RECHECK_INTERVAL_MS = 500;

  // Central store. Buttons, the count badge and the drawer list subscribe to it.
  var WishlistStore = {
    ready: false,
    swat: null,
    productIds: new Set(), // empi values currently on the wishlist
    items: [], // raw Swym items: [{ epi, empi, du, dt, iu, pr }]
    listeners: new Set(),

    subscribe: function (fn) {
      this.listeners.add(fn);
      if (this.ready) {
        fn();
      }
      var self = this;
      return function () {
        self.listeners.delete(fn);
      };
    },

    notify: function () {
      this.listeners.forEach(function (fn) {
        try {
          fn();
        } catch (error) {
          // A single broken subscriber must not break the others.
        }
      });
    },

    has: function (productId) {
      return this.productIds.has(String(productId));
    },

    setItems: function (items) {
      this.items = Array.isArray(items) ? items : [];
      this.productIds = new Set(
        this.items.map(function (item) {
          return String(item.empi);
        })
      );
      this.notify();
    },

    // Merges a single item into the store. Driven by the Swym add event, which
    // also fires for our own optimistic adds, so it must be idempotent.
    applyAdd: function (item) {
      if (!item || item.empi == null) {
        return;
      }
      var empiKey = String(item.empi);
      var changed = false;
      if (!this.productIds.has(empiKey)) {
        this.productIds.add(empiKey);
        changed = true;
      }
      var inItems = this.items.some(function (existing) {
        return String(existing.empi) === empiKey;
      });
      if (!inItems) {
        this.items = this.items.concat([item]);
        changed = true;
      }
      if (changed) {
        this.notify();
      }
    },

    // Removes a product from the store. Idempotent mirror of applyAdd.
    applyRemove: function (empi) {
      if (empi == null) {
        return;
      }
      var empiKey = String(empi);
      var changed = this.productIds.delete(empiKey);
      var lengthBefore = this.items.length;
      this.items = this.items.filter(function (item) {
        return String(item.empi) !== empiKey;
      });
      if (changed || this.items.length !== lengthBefore) {
        this.notify();
      }
    },

    add: function (payload) {
      if (!this.swat) {
        reportError('add-no-sdk', new Error('Wishlist SDK not ready'), { empi: payload.empi });
        return;
      }
      // Optimistic update for heart and drawer alike, reverted if the SDK call
      // fails. The payload (empi/epi/du) is all the drawer rendering needs.
      this.applyAdd(payload);
      try {
        this.swat.addToWishList(payload, function () {}, function (error) {
          WishlistStore.applyRemove(payload.empi);
          reportError('add-failed', error, { empi: payload.empi });
        });
      } catch (error) {
        WishlistStore.applyRemove(payload.empi);
        reportError('add-threw', error, { empi: payload.empi });
      }
    },

    remove: function (payload) {
      if (!this.swat) {
        reportError('remove-no-sdk', new Error('Wishlist SDK not ready'), { empi: payload.empi });
        return;
      }
      var empiKey = String(payload.empi);
      // Keep the removed rows so a failed SDK call can fully restore the state.
      var removedItems = this.items.filter(function (item) {
        return String(item.empi) === empiKey;
      });
      this.productIds.delete(empiKey);
      this.items = this.items.filter(function (item) {
        return String(item.empi) !== empiKey;
      });
      this.notify();

      function revert() {
        WishlistStore.productIds.add(empiKey);
        WishlistStore.items = WishlistStore.items.concat(removedItems);
        WishlistStore.notify();
      }

      try {
        this.swat.removeFromWishList(payload, function () {}, function (error) {
          revert();
          reportError('remove-failed', error, { empi: payload.empi });
        });
      } catch (error) {
        revert();
        reportError('remove-threw', error, { empi: payload.empi });
      }
    },

    refetch: function () {
      if (!this.swat) {
        return;
      }
      try {
        this.swat.fetch(
          function (items) {
            WishlistStore.setItems(items);
          },
          function (error) {
            reportError('fetch-failed', error);
          }
        );
      } catch (error) {
        reportError('fetch-threw', error);
      }
    },

    // Opens the wishlist drawer (used to surface the consent notice when the
    // SDK has not loaded because cookies were not accepted yet).
    openDrawer: function () {
      var drawer = document.getElementById('wishlist-drawer');
      if (!drawer || typeof drawer.show !== 'function') {
        return;
      }
      try {
        drawer.show();
      } catch (error) {
        reportError('open-drawer-failed', error);
      }
    },

    closeDrawer: function () {
      var drawer = document.getElementById('wishlist-drawer');
      if (drawer && typeof drawer.hide === 'function') {
        try {
          drawer.hide();
        } catch (error) {
          // Closing is best-effort.
        }
      }
    }
  };

  // Boot the SDK. All SDK access must go through SwymCallbacks.
  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(function (swat) {
    WishlistStore.swat = swat;
    WishlistStore.ready = true;

    // Fail loud on a Swym SDK version mismatch (the methods we depend on).
    ['addToWishList', 'removeFromWishList', 'fetch'].forEach(function (method) {
      if (typeof swat[method] !== 'function') {
        reportError(
          'sdk-method-missing',
          new Error('swat.' + method + ' is not a function (Swym SDK version mismatch?)')
        );
      }
    });

    WishlistStore.refetch();

    // Keep the store in sync when items change (own calls and Swym UI alike).
    // Apply the event payload directly instead of refetching: right after an
    // add, swat.fetch still returns the previous (cached) list, which would
    // wipe the optimistic state and un-fill the heart until a second click.
    if (swat.evtLayer && typeof swat.evtLayer.addEventListener === 'function') {
      swat.evtLayer.addEventListener('sw:addedtowishlist', function (event) {
        var item = event && event.detail && event.detail.d;
        if (item && item.empi != null) {
          WishlistStore.applyAdd(item);
        } else {
          WishlistStore.refetch();
        }
      });
      swat.evtLayer.addEventListener('sw:removedfromwishlist', function (event) {
        var item = event && event.detail && event.detail.d;
        if (item && item.empi != null) {
          WishlistStore.applyRemove(item.empi);
        } else {
          WishlistStore.refetch();
        }
      });
    }

    WishlistStore.notify();
  });

  // The Swym SDK is consent-gated: without accepted cookies it never loads. That
  // is an expected state (the drawer shows a consent notice), not an error, so we
  // only leave a quiet debug trace.
  setTimeout(function () {
    if (!WishlistStore.ready) {
      logDebug('sdk-not-loaded', 'Swym SDK not initialized within 8s (cookie consent likely not granted).');
    }
  }, 8000);

  // Exposed for the drawer markup and for debugging during development.
  window.ZahnheldWishlist = WishlistStore;

  // Swym wishlist reminder / retargeting emails link back with ?swaction=openui
  // to surface the saved items. This theme replaces Swym's default UI with a
  // custom drawer, so we intercept the parameter and open our drawer instead.
  // The drawer shows its loading / empty / consent state immediately and fills
  // in as the SDK loads, exactly like clicking the header heart.
  (function handleOpenUiParam() {
    try {
      var params = new URLSearchParams(window.location.search);
      if (params.get('swaction') !== 'openui') {
        return;
      }
      // Strip the parameter so a refresh or back navigation does not reopen it.
      params.delete('swaction');
      var query = params.toString();
      var cleanUrl = window.location.pathname + (query ? '?' + query : '') + window.location.hash;
      window.history.replaceState(window.history.state, '', cleanUrl);

      // The drawer is an x-drawer custom element defined in theme.js (a module),
      // while this is a deferred classic script, so wait until it is registered
      // to guarantee .show() is available regardless of script load order.
      window.customElements.whenDefined('x-drawer').then(function () {
        WishlistStore.openDrawer();
      });
    } catch (error) {
      reportError('open-ui-param-failed', error);
    }
  })();

  // <wishlist-button> toggles a single product on the wishlist.
  var WishlistButton = class extends HTMLElement {
    connectedCallback() {
      this.control = this.querySelector('button') || this;
      this._onClick = this._onClick.bind(this);
      this._sync = this._sync.bind(this);
      this.addEventListener('click', this._onClick);
      this._unsubscribe = WishlistStore.subscribe(this._sync);
      this._sync();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      if (this._unsubscribe) {
        this._unsubscribe();
      }
    }

    get payload() {
      return {
        empi: parseInt(this.getAttribute('data-wishlist-product-id'), 10),
        epi: parseInt(this.getAttribute('data-wishlist-variant-id'), 10),
        du: this.getAttribute('data-wishlist-url')
      };
    }

    get pressed() {
      return WishlistStore.has(this.getAttribute('data-wishlist-product-id'));
    }

    _sync() {
      var pressed = this.pressed;
      this.control.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      var label = pressed
        ? this.getAttribute('data-label-remove')
        : this.getAttribute('data-label-add');
      if (label) {
        this.control.setAttribute('aria-label', label);
      }
    }

    _onClick(event) {
      // The button often sits inside a product link, so stop the navigation.
      event.preventDefault();
      event.stopPropagation();
      if (!WishlistStore.ready) {
        // SDK not ready, so we cannot save yet. Open the drawer so the shopper
        // sees the right state: the cookie notice when consent is missing, or the
        // empty / loading state when consent is granted. Not an error.
        logDebug('click-before-ready', 'SDK not ready, opening drawer.');
        WishlistStore.openDrawer();
        return;
      }
      if (this.pressed) {
        WishlistStore.remove(this.payload);
      } else {
        WishlistStore.add(this.payload);
      }
    }
  };

  if (!window.customElements.get('wishlist-button')) {
    window.customElements.define('wishlist-button', WishlistButton);
  }

  // <wishlist-count> mirrors the number of saved products (header badge).
  var WishlistCount = class extends HTMLElement {
    connectedCallback() {
      this._sync = this._sync.bind(this);
      this._unsubscribe = WishlistStore.subscribe(this._sync);
      this._sync();
    }

    disconnectedCallback() {
      if (this._unsubscribe) {
        this._unsubscribe();
      }
    }

    _sync() {
      var count = WishlistStore.productIds.size;
      this.textContent = count;
      this.classList.toggle('opacity-0', count === 0);
    }
  };

  if (!window.customElements.get('wishlist-count')) {
    window.customElements.define('wishlist-count', WishlistCount);
  }

  // <wishlist-list> renders the drawer body: empty-state vs item rows.
  // Each row is the real theme line item, fetched via the Section Rendering API,
  // so it carries the sale badge, compare-at price and unit price like everywhere else.
  var WishlistList = class extends HTMLElement {
    connectedCallback() {
      this._sync = this._sync.bind(this);
      this._onListClick = this._onListClick.bind(this);
      this.consentEl = this.querySelector('[data-wishlist-consent]');
      this.noticeEl = document.querySelector('[data-wishlist-notice]');
      this.emptyEl = this.querySelector('[data-wishlist-empty]');
      this.listEl = this.querySelector('[data-wishlist-items]');
      this._renderedKey = null;
      if (this.listEl) {
        this.listEl.addEventListener('click', this._onListClick);
      }
      if (this.consentEl) {
        // Reopen the cookie banner (Pandectes #reopenBanner) and close the drawer
        // so the banner is visible. Do not preventDefault, the CMP needs the click.
        this.consentEl.addEventListener('click', function (event) {
          if (event.target.closest('a[href="#reopenBanner"]')) {
            WishlistStore.closeDrawer();
          }
        });
      }
      // Re-sync when the shopper makes a consent choice (Shopify-native event,
      // reliable even though Pandectes emits none) so the cookie notice clears
      // the moment consent is granted, without needing the SDK to have loaded.
      document.addEventListener('visitorConsentCollected', this._sync);
      this._unsubscribe = WishlistStore.subscribe(this._sync);
      this._sync();

      // The Customer Privacy API loads asynchronously and fires no event when it
      // becomes available (visitorConsentCollected only fires on a fresh consent
      // choice, not on reload). If consent is still unknown at connect time,
      // re-check a few times so an already-consented returning visitor is never
      // left on the cookie notice, even in the edge case where the SDK never boots.
      if (wishlistConsentGranted() === null) {
        var self = this;
        var attempts = 0;
        var recheck = function () {
          self._sync();
          attempts += 1;
          if (wishlistConsentGranted() === null && attempts < CONSENT_RECHECK_ATTEMPTS) {
            self._consentTimer = setTimeout(recheck, CONSENT_RECHECK_INTERVAL_MS);
          }
        };
        this._consentTimer = setTimeout(recheck, CONSENT_RECHECK_INTERVAL_MS);
      }
    }

    disconnectedCallback() {
      if (this.listEl) {
        this.listEl.removeEventListener('click', this._onListClick);
      }
      document.removeEventListener('visitorConsentCollected', this._sync);
      if (this._consentTimer) {
        clearTimeout(this._consentTimer);
      }
      if (this._unsubscribe) {
        this._unsubscribe();
      }
    }

    _sync() {
      var ready = WishlistStore.ready;
      var items = WishlistStore.items;
      var hasItems = ready && items.length > 0;

      // The cookie notice is only correct when consent is genuinely missing.
      // If the shopper has already consented the SDK may simply still be loading
      // (or its app embed is disabled); either way we must not ask them to accept
      // cookies again. Unknown consent (null) keeps the prior behaviour, so a
      // shopper who has not consented still sees the notice they need.
      var consentGranted = wishlistConsentGranted();
      var showConsent = !ready && consentGranted !== true;
      var showEmpty = !showConsent && !hasItems;

      if (this.consentEl) {
        this.consentEl.hidden = !showConsent;
      }
      // The guest session notice is meaningful only once the SDK is available.
      if (this.noticeEl) {
        this.noticeEl.hidden = !ready;
      }
      if (this.emptyEl) {
        this.emptyEl.hidden = !showEmpty;
      }
      if (this.listEl) {
        this.listEl.hidden = !hasItems;
        if (ready) {
          this._render(items);
        }
      }
    }

    _render(items) {
      var self = this;
      var key = items
        .map(function (item) {
          return item.empi;
        })
        .join(',');
      if (key === this._renderedKey) {
        return; // nothing changed, avoid refetching the sections
      }
      this._renderedKey = key;

      if (!items.length) {
        this.listEl.replaceChildren();
        return;
      }

      Promise.all(
        items.map(function (item) {
          return self._fetchLineItem(item);
        })
      ).then(function (nodes) {
        if (key !== self._renderedKey) {
          return; // a newer render started while we were fetching
        }
        self.listEl.replaceChildren.apply(self.listEl, nodes.filter(Boolean));
      }).catch(function (error) {
        reportError('render-failed', error);
      });
    }

    _fetchLineItem(item) {
      if (!item.du) {
        return Promise.resolve(null);
      }
      // Normalise to a same-origin request: take only the path and query of the
      // stored URL and resolve it against the current origin. This structurally
      // prevents any cross-origin fetch and also works on preview/myshopify
      // domains where shop.url (stored in du) differs from the current host.
      var url;
      try {
        var parsed = new URL(item.du, window.location.origin);
        parsed.searchParams.set('section_id', 'wishlist-line-item');
        url = parsed.pathname + parsed.search;
      } catch (error) {
        reportError('line-item-bad-url', error, { du: item.du });
        return Promise.resolve(null);
      }
      return fetch(url)
        .then(function (response) {
          if (!response.ok) {
            reportError('line-item-http', new Error('HTTP ' + response.status), { url: url });
            return '';
          }
          return response.text();
        })
        .then(function (html) {
          if (!html) {
            return null;
          }
          // Parse the same-origin section markup. DOMParser does not execute
          // scripts, and the content is our own theme HTML from the Section
          // Rendering API. Import only the inner row, the shopify-section
          // wrapper would duplicate its id across rows.
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var row = parsed.querySelector('.wishlist-line-item');
          if (!row) {
            reportError('line-item-empty', new Error('No .wishlist-line-item in section response'), { url: url });
          }
          return row ? document.importNode(row, true) : null;
        })
        .catch(function (error) {
          reportError('line-item-fetch', error, { url: url });
          return null;
        });
    }

    _onListClick(event) {
      var button = event.target.closest('.wishlist-line-item__remove');
      if (!button) {
        return;
      }
      event.preventDefault();

      var payload = {
        empi: parseInt(button.getAttribute('data-wishlist-product-id'), 10),
        epi: parseInt(button.getAttribute('data-wishlist-variant-id'), 10),
        du: button.getAttribute('data-wishlist-url')
      };

      var row = button.closest('.wishlist-line-item');

      // Ignore repeat clicks while this row's removal animation is already
      // running; the in-flight animation triggers the store removal itself.
      if (row && row.dataset.removing === 'true') {
        return;
      }

      var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // No row or reduced motion: remove immediately without the animation.
      if (!row || reduceMotion) {
        WishlistStore.remove(payload);
        return;
      }

      this._animateRemoval(row, function () {
        WishlistStore.remove(payload);
      });
    }

    // Pop the row out before removing it from the store. Two stages so the
    // scale-up "pop" is not clipped: first a quick scale + fade with no overflow
    // change, then a collapse of the (now invisible) slot so the list closes.
    // WishlistStore.remove only re-renders once the store changes, so the exit
    // plays first; a failed SDK call reverts the store and re-renders the item.
    _animateRemoval(row, done) {
      row.dataset.removing = 'true';
      row.style.pointerEvents = 'none';

      var listGap = this.listEl ? parseFloat(getComputedStyle(this.listEl).rowGap) : 0;
      if (isNaN(listGap)) {
        listGap = 0;
      }

      var collapsed = false;
      var collapse = function () {
        if (collapsed) {
          return;
        }
        collapsed = true;
        row.removeEventListener('animationend', onPop);

        // Lock the layout height (transform does not affect offsetHeight), then
        // collapse the slot. The content is already invisible, so clipping is fine.
        row.style.height = row.offsetHeight + 'px';
        void row.offsetHeight;
        row.classList.remove('wishlist-line-item--popping');
        row.classList.add('wishlist-line-item--collapsing');
        row.style.height = '0px';
        row.style.paddingBlock = '0px';
        row.style.borderBlockWidth = '0px';
        // Pull the following item up by one list gap so the row leaves no empty slot.
        row.style.marginBlockEnd = '-' + listGap + 'px';

        var removed = false;
        var finish = function () {
          if (removed) {
            return;
          }
          removed = true;
          row.removeEventListener('transitionend', onCollapse);
          done();
        };
        var onCollapse = function (transitionEvent) {
          if (transitionEvent.target === row && transitionEvent.propertyName === 'height') {
            finish();
          }
        };
        row.addEventListener('transitionend', onCollapse);
        setTimeout(finish, 400); // safety net
      };

      var onPop = function (animationEvent) {
        if (animationEvent.target === row && animationEvent.animationName === 'wishlist-line-item-pop') {
          collapse();
        }
      };

      row.addEventListener('animationend', onPop);
      row.classList.add('wishlist-line-item--popping');
      setTimeout(collapse, 400); // safety net if animationend never fires
    }
  };

  if (!window.customElements.get('wishlist-list')) {
    window.customElements.define('wishlist-list', WishlistList);
  }
})();
